import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from './Button.jsx'
import ConfirmDialog from './ConfirmDialog.jsx'

/**
 * The bar that appears while picking posts: select all, how many, approve, remove.
 *
 * Shared by the calendar and the queue because it is the same decision in both places, and two
 * copies of it would drift — one of them would learn to ask before removing and the other would
 * not. It sits above the thumb on a phone and inside the page's measure on a desk; either way it
 * is the only thing that acts on a selection, so it is where the eye already is.
 */
export default function SelectionBar({ selected, allIds, busy, onSelect, onApprove, onRemove }) {
  const { t } = useTranslation()
  const [asking, setAsking] = useState(false)

  const all = allIds.length > 0 && selected.size === allIds.length

  return (
    <>
      <div className="pickbar">
        <button
          type="button"
          className="button button--quiet"
          onClick={() => onSelect(all ? new Set() : new Set(allIds))}
        >
          {all ? t('calendar.selectNone') : t('calendar.selectAll')}
        </button>

        <span className="pickbar__count">{t('calendar.selected', { count: selected.size })}</span>

        <Button
          type="button"
          className="button"
          disabled={busy || selected.size === 0}
          loading={busy && !asking}
          onClick={onApprove}
        >
          {t('calendar.approve')}
        </Button>

        <Button
          type="button"
          className="button button--danger"
          disabled={busy || selected.size === 0}
          onClick={() => setAsking(true)}
        >
          {t('calendar.remove')}
        </Button>
      </div>

      {/* Removing is the one bulk action that cannot be taken back, so it asks — in-app. */}
      <ConfirmDialog
        open={asking}
        title={t('calendar.removeAsk', { count: selected.size })}
        body={t('calendar.removeBody')}
        confirmLabel={t('calendar.remove')}
        destructive
        onConfirm={() => {
          setAsking(false)
          onRemove()
        }}
        onCancel={() => setAsking(false)}
      />
    </>
  )
}
